import { Link, useRouteLoaderData, useSubmit } from "react-router-dom";
//--------------------------------------------------------------------------------------------
import UserInfo from "src/components/common/userInfo";
import { testerEmails } from "src/components/common/functions";
import { Contact } from "./types";
import { useAuthStore, User } from "../main/authStore";
//--------------------------------------------------------------------------------------------
type FieldDef = {
    label: string;
    value?: React.ReactNode;
    show?: boolean;
};

export default function ContactDetail() {
    const user = useAuthStore(state => state.user) as User;
    const contact = useRouteLoaderData<Contact>("contact")!;
    // ------------------------------------------------------------------
    // Placeholder related data (already in your original file)
    const submit = useSubmit();
    // ------------------------------------------------------------------
    const handleDelete = async (id: number) => {
        if(!window.confirm("Are you sure to delete this contact?\n\nThis action cannot be undone.")) return;
        const formData = new FormData();
        formData.append("id", String(id));
        submit(formData, {
            method: "post",
            action: "Delete"
        });
    };
    const handleValidate = (id:number) => {
        if(!window.confirm("Are you sure to validate this contact?")) return;
        let validateReason = window.prompt("reason to validdate:");
        if(validateReason === null) return;
        while(validateReason === ""){
            window.alert("Reason is required to validate.");
            validateReason = window.prompt("reason to validate:");
            if(validateReason === null) return;
        }
        const formData = new FormData();
        formData.append("validateReason", validateReason);
        formData.append("id", String(id));
        submit(formData, {
            method: "post",
            action: "Validate"
        });
    }
    const handleVoid = (id: number) => {
        if(!window.confirm("Are you sure to void this contact?")) return;
        let voidReason = window.prompt("reason to void:");
        if(voidReason === null) return;
        while(voidReason === ""){
            window.alert("Reason is required to void.");
            voidReason = window.prompt("reason to void:");
            if(voidReason === null) return;
        }
        const formData = new FormData();
        formData.append("voidReason", voidReason);
        formData.append("id", String(id));
        submit(formData, {
            method: "post",
            action: "Void"
        });
    }
    const address = [ contact.street, contact.city, contact.state, contact.zipCode ].filter(Boolean).join(", ");
    const fields: FieldDef[] = [
        { label: "Contact ID", value: contact.id },
        { label: "Contact Name", value: contact.name },
        { label: "Organization Name", value: <Link to = {`/Organizations/${contact.organizationId}`}>{contact.organizationName}</Link> },
        { label: "Role", value: contact.role },
        { label: "Email 1", value: contact.email1 && <a href = {`mailto:${contact.email1}`} children = {contact.email1}/> },
        { label: "Email 2", value: contact.email2 && <a href = {`mailto:${contact.email2}`} children = {contact.email2}/> },
        { label: "Business Phone", value: contact.phoneNumber?
            <a href = {`tel:+1${contact.phoneNumber.replace(/-/g, "")}${contact.extension?`,${contact.extension}`:""}`}>
                {contact.phoneNumber}{contact.extension?` Ext. ${contact.extension}`:null}
            </a>
            :<></>        
        },
        { label: "Fax", value: contact.fax?
            <a href = {"fax:+1" + contact.fax.replace(/-/g, "")}>{contact.fax}</a>
            :<></>
        },
        { label: "Direct Number", value: contact.directNumber?
            <a href = {`tel:+1${contact.directNumber.replace(/-/g, "")}${contact.extension?`,${contact.extension}`:""}`}>
                {contact.directNumber}
            </a>
            :<></>        
         },
        { label: "Address", value: <a href = {`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`} target = "_blank" rel="noreferrer">{address}</a>},
        { label: "Oversea Address", value: <a href = {`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contact.overseaAddress)}`} target = "_blank" rel="noreferrer">{contact.overseaAddress}</a>},
        {
            label: "Creator",
            show: !!contact.creatorId,
            value: (
                <>
                    <UserInfo
                        name={contact.creatorName}
                        id={contact.creatorId}
                    />
                    <span className="ms-2 text-muted">
                        {contact.createdAt}
                    </span>
                </>
            )
        },
        {
            label: "Updater",
            show: !!contact.updaterId,
            value: (
                <>
                    <UserInfo
                        name={contact.updaterName}
                        id={contact.updaterId}
                    />
                    <span className="ms-2 text-muted">
                        {contact.updatedAt}
                    </span>
                </>
            )
        },
        {
            label: "Void Reason",
            show: !!contact.voidReason,
            value: contact.voidReason
        },
        {
            label: "Validation Reason",
            show: !!contact.validateReason,
            value: contact.validateReason
        }
    ];
    // ------------------------------------------------------------------
    return (
        <div>
            <div className="cssSeperateWhenWide ps-3 pt-3">
                <table className="table table-fixed me-3">
                    <tbody className="noTableTopBorder">
                        {fields
                            .filter(f => f.value && f.show !== false)
                            .map((f, idx) => (
                                <tr key={idx}>
                                    <th>{f.label}</th>
                                    <td>{f.value}</td>
                                </tr>
                            ))}
                        {contact.projects.length > 0 && (
                            <tr>
                                <th>Projects</th>
                                <td className="position-relative">
                                    {contact.projects.slice(0, 5).map(project => (
                                        <li key={project.id}>
                                            <Link to={`/Projects/${project.id}`}>
                                                {project.name}
                                            </Link>
                                        </li>
                                    ))}
                                    {contact.projects.length > 5? 
                                    <li><Link to = {`/Projects?contactId=${contact.id}`} className="text-warning">More...</Link></li>
                                    :null}
                                </td>
                            </tr>
                        )}
                        {contact.opportunities.length > 0 && (
                            <tr>
                                <th>Opportunities</th>
                                <td className="position-relative">
                                    {contact.opportunities.slice(0, 5).map(opportunity => (
                                        <li key={opportunity.id}>
                                            <Link to={`/Projects/${opportunity.id}`}>
                                                {opportunity.name}
                                            </Link>
                                        </li>
                                    ))}
                                    {contact.opportunities.length > 5? 
                                    <li><Link to = {`/Projects?contactId=${contact.id}`} className="text-warning">More...</Link></li>
                                    :null}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                <div className="d-flex flex-column flex-grow-1 me-3" style={{minWidth: "40%", minHeight: "100px"}}>
                    <fieldset className="border rounded flex-grow-1 px-3">
                        <legend className="fw-bold">
                            Background
                        </legend>
                        <span className = "cssNotes">
                            {contact.background}
                        </span>
                    </fieldset>
                </div>
            </div>
            <div className="my-3">
            </div>
            <div className="my-3">
                {contact.void === "yes"?<>
                    {testerEmails.includes(user.email)?
                    <button
                        children = "Validate"
                        className = "btn btn-primary"
                        onClick = {() => handleValidate(contact.id)}
                    />:null}
                </>:
                <>
                <Link
                    children = "Edit"
                    to = "Edit"
                    className = "btn btn-outline-primary ms-3"
                />
                <button
                    children = "Void"
                    type = "button"
                    className = "btn btn-danger ms-3"
                    onClick = {() => handleVoid(contact.id)}
                />
                </>}
                {contact.void === "yes"?
                <button
                    children = "Delete"
                    className = "btn btn-danger ms-3"
                    onClick = {() => handleDelete(contact.id)}
                />:null}
                <Link
                    children = "Back"
                    to = ".."
                    relative = "path"
                    className = "btn btn-outline-secondary float-end"
                />
            </div>
        </div>
    );
}